import React from "react";
import Button from "../Buttons/Button/Button";
import { ChevronLeft, ChevronRight } from "lucide-react";
import "./Pagination.css";

/**
 * Reusable numbered pagination with ellipsis and Prev/Next controls.
 *
 * Props:
 * - page: number (current page, 1-based)
 * - totalPages: number
 * - onPageChange: (nextPage: number) => void
 * - infoLeft?: ReactNode (legacy, optional block rendered above-left)
 * - infoBelow?: ReactNode (optional block rendered below the controls)
 * - size?: 'sm' | 'md' (button size)
 * - className?: string (wrapper extra class)
 */
export default function Pagination({
  page,
  totalPages,
  onPageChange,
  infoLeft,
  infoBelow,
  size = "sm",
  className = "",
}) {
  const clamp = (n) => Math.max(1, Math.min(totalPages, n));

  const getPageItems = (current, total) => {
    const pages = [];
    const add = (p) => pages.push(p);
    const windowSize = 1;
    if (total <= 7) {
      for (let i = 1; i <= total; i++) add(i);
      return pages;
    }
    add(1);
    if (current > 2 + windowSize) add("…");
    const start = Math.max(2, current - windowSize);
    const end = Math.min(total - 1, current + windowSize);
    for (let i = start; i <= end; i++) add(i);
    if (current < total - (1 + windowSize)) add("…");
    add(total);
    return pages;
  };

  if (!totalPages || totalPages < 1) return null;

  return (
    <div className={`owly-pagination ${className}`}>
      {infoLeft && (
        <div className="owly-pagination-info owly-pagination-info--top">{infoLeft}</div>
      )}
      <div className="owly-pagination-controls">
        <Button
          variant="secondary"
          size={size}
          onClick={() => onPageChange(clamp(page - 1))}
          disabled={page === 1}
          leftIcon={<ChevronLeft size={16} />}
        />

        <div className="owly-page-numbers">
          {getPageItems(page, totalPages).map((item, idx) =>
            item === "…" ? (
              <span key={`ellipsis-${idx}`} className="owly-page-ellipsis">…</span>
            ) : (
              <Button
                key={`page-${item}`}
                variant={item === page ? "primary" : "secondary"}
                size={size}
                onClick={() => onPageChange(item)}
              >
                {item}
              </Button>
            )
          )}
        </div>

        <Button
          variant="secondary"
          size={size}
          onClick={() => onPageChange(clamp(page + 1))}
          disabled={page === totalPages}
          leftIcon={<ChevronRight size={16} />}
        />
      </div>
      {infoBelow && (
        <div className="owly-pagination-info owly-pagination-info--bottom">{infoBelow}</div>
      )}
    </div>
  );
}
